import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, User as UserIcon, Mail, Phone, MapPin, ShieldOff, Shield, KeyRound, Edit3, Activity, FileText, CreditCard, Truck } from "lucide-react";

const fmt = (v) => v ? new Date(v).toLocaleString("fr-FR") : "—";
const fmtEur = (c) => `${(Number(c || 0) / 100).toFixed(2)} €`;

export default function AdminUserDetailPage() {
  const { user_id } = useParams();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/users/${user_id}/full`);
      setData(r.data);
      setForm({
        first_name: r.data.user.first_name || "",
        last_name: r.data.user.last_name || "",
        email: r.data.user.email || "",
        phone: r.data.user.phone || "",
      });
    } catch (e) { toast.error("Chargement impossible"); }
  }, [user_id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.patch(`/admin/users/${user_id}`, { ...form, reason });
      toast[res.changed ? "success" : "info"](res.changed ? "Modifications enregistrées" : "Aucun changement");
      setEditing(false); setReason(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const block = async () => {
    if (!blockReason.trim()) { toast.error("Motif obligatoire"); return; }
    try {
      await api.post(`/admin/users/${user_id}/block`, { reason: blockReason });
      toast.success("Compte bloqué"); setBlocking(false); setBlockReason(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const unblock = async () => {
    if (!window.confirm("Débloquer ce compte ?")) return;
    try {
      await api.post(`/admin/users/${user_id}/unblock`);
      toast.success("Compte débloqué"); load();
    } catch (e) { toast.error("Erreur"); }
  };
  const sendReset = async () => {
    if (!window.confirm("Envoyer un email de réinitialisation à cet utilisateur ?")) return;
    try {
      await api.post(`/admin/users/${user_id}/send-password-reset`);
      toast.success("Email de réinitialisation envoyé"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  if (!data) return <div>Chargement…</div>;
  const { user: u, cases, payments, shipments, audit_logs } = data;
  const fullPay = payments.filter((p) => p.paypal_capture_id);

  return (
    <div className="space-y-6" data-testid="admin-user-detail">
      <div className="flex items-center gap-3">
        <Link to="/admin/utilisateurs"><Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Button></Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">{u.first_name} {u.last_name}</h1>
          <div className="text-xs text-muted-foreground font-mono">{u.email} · id={u.id}</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-user-btn"><Edit3 className="h-3 w-3 mr-1" /> Modifier</Button>
          <Button variant="outline" size="sm" onClick={sendReset} data-testid="reset-pwd-btn"><KeyRound className="h-3 w-3 mr-1" /> Envoyer reset mdp</Button>
          {u.blocked
            ? <Button variant="outline" size="sm" onClick={unblock} data-testid="unblock-btn"><Shield className="h-3 w-3 mr-1" /> Débloquer</Button>
            : <Button variant="destructive" size="sm" onClick={() => setBlocking(true)} data-testid="block-btn"><ShieldOff className="h-3 w-3 mr-1" /> Bloquer</Button>}
        </div>
      </div>

      {u.blocked && (
        <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 p-3 text-sm">
          <div className="font-semibold text-red-800 dark:text-red-200">Compte bloqué</div>
          <div className="text-xs text-red-700 dark:text-red-300">
            Depuis {fmt(u.blocked_at)} — motif : {u.blocked_reason || "—"}
          </div>
        </div>
      )}

      <Section icon={UserIcon} title="Identité & coordonnées">
        <Grid>
          <Info label="Prénom" value={u.first_name} />
          <Info label="Nom" value={u.last_name} />
          <Info label="Email" value={u.email} icon={Mail} />
          <Info label="Téléphone" value={u.phone} icon={Phone} />
          <Info label="Rôle" value={<Badge>{u.role}</Badge>} raw />
          <Info label="Compte" value={u.blocked ? <Badge variant="destructive">Bloqué</Badge> : <Badge variant="outline">Actif</Badge>} raw />
        </Grid>
      </Section>

      <Section icon={Shield} title="Authentification & sécurité">
        <Grid>
          <Info label="ID" value={u.id} mono />
          <Info label="Créé le" value={fmt(u.created_at)} />
          <Info label="Email vérifié" value={u.email_verified ? `Oui — ${fmt(u.email_verified_at)}` : "Non"} />
          <Info label="Dernière connexion" value={fmt(u.last_login_at)} />
          <Info label="Dernière IP" value={u.last_ip || "—"} icon={MapPin} mono />
          <Info label="User-Agent" value={(u.last_user_agent || "—").slice(0, 60)} mono />
          <Info label="Connexions réussies" value={u.login_count || 0} />
          <Info label="Échecs" value={u.failed_login_count || 0} />
          <Info label="Dernier reset admin" value={fmt(u.last_admin_reset_at)} />
        </Grid>
      </Section>

      <Section icon={FileText} title={`Dossiers (${cases.length})`}>
        {cases.length === 0 ? <div className="text-sm text-muted-foreground">Aucun dossier.</div> : (
          <div className="text-sm space-y-1 max-h-72 overflow-y-auto">
            {cases.slice(0, 30).map((c) => (
              <Link key={c.id} to={`/admin/cases/${c.id}`} className="flex items-center justify-between border border-border rounded-md p-2 hover:bg-secondary/40">
                <div className="min-w-0">
                  <div className="font-mono text-xs text-muted-foreground">{c.id.slice(0, 8)}</div>
                  <div className="font-medium truncate">{c.organization_name || "—"}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge variant="outline">{c.status}</Badge>
                  <div className="font-mono text-xs">{fmtEur(c.price_cents || 0)}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>

      <Section icon={CreditCard} title={`Paiements PayPal (${fullPay.length} capturés / ${payments.length} total)`}>
        {payments.length === 0 ? <div className="text-sm text-muted-foreground">Aucun paiement.</div> : (
          <div className="text-xs space-y-1 max-h-60 overflow-y-auto">
            {payments.map((p) => (
              <div key={p.id} className="border border-border rounded-md p-2">
                <div className="flex justify-between mb-1">
                  <span className="font-mono">{p.paypal_order_id || p.id.slice(0, 12)}</span>
                  <Badge variant={p.status === "PAYMENT_COMPLETED" ? "default" : "outline"}>{p.status}</Badge>
                </div>
                <div className="text-muted-foreground">env={p.environment} · capture_id={p.paypal_capture_id || "—"} · {fmtEur(p.amount_captured_cents || p.amount_cents)}</div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 text-xs text-muted-foreground italic">Ces données PayPal ne sont pas modifiables manuellement.</div>
      </Section>

      <Section icon={Truck} title={`Courriers postaux (${shipments.length})`}>
        {shipments.length === 0 ? <div className="text-sm text-muted-foreground">Aucun envoi postal.</div> : (
          <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
            {shipments.map((s) => (
              <li key={s.id} className="border border-border rounded-md p-2 flex justify-between">
                <span>{s.provider} / {s.postal_environment} — ref={s.provider_reference || "—"}</span>
                <Badge variant="outline">{s.status}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section icon={Activity} title="Journal d'audit (50 dernières)">
        {audit_logs.length === 0 ? <div className="text-sm text-muted-foreground">Aucune entrée.</div> : (
          <ol className="text-xs space-y-1 max-h-60 overflow-y-auto">
            {audit_logs.map((l, i) => (
              <li key={i} className="border-l-2 border-orange-400/60 pl-2 py-1">
                <div className="font-mono text-muted-foreground">{fmt(l.created_at || l.at)}</div>
                <div><b>{l.action}</b> — {l.entity || l.target_type} ({l.actor_role})</div>
              </li>
            ))}
          </ol>
        )}
      </Section>

      {/* Edit dialog */}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>Modifier l&apos;utilisateur</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <Input placeholder="Prénom" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            <Input placeholder="Nom" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <Input placeholder="Motif (obligatoire pour audit)" value={reason} onChange={(e) => setReason(e.target.value)} />
            <div className="text-xs text-muted-foreground italic">Changer l&apos;email force la re-vérification automatique. Aucun paiement PayPal existant n&apos;est modifié.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="save-user-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block dialog */}
      <Dialog open={blocking} onOpenChange={setBlocking}>
        <DialogContent data-testid="block-user-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-700"><ShieldOff className="h-4 w-4" /> Bloquer le compte</DialogTitle></DialogHeader>
          <div className="text-sm space-y-3">
            <div className="rounded-md bg-orange-50 border border-orange-200 p-3 text-xs">
              L&apos;utilisateur ne pourra plus se connecter, mais ses dossiers, paiements et documents sont conservés.
            </div>
            <Input placeholder="Motif obligatoire" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} data-testid="block-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlocking(false)}>Annuler</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={block} data-testid="confirm-block-btn">Confirmer le blocage</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="card-elevated p-5">
      <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">{Icon && <Icon className="h-4 w-4 text-primary" />}{title}</h2>
      {children}
    </div>
  );
}
function Grid({ children }) { return <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">{children}</dl>; }
function Info({ label, value, icon: Icon, mono, raw }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">{Icon && <Icon className="h-3 w-3" />}{label}</span>
      {raw ? <span>{value}</span> : <span className={`${mono ? "font-mono text-xs break-all" : ""}`}>{value ?? "—"}</span>}
    </div>
  );
}
